import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { toast } from "sonner";
import { openWhatsApp } from "@/lib/api";
import { trackLeaseFormSubmit } from "@/lib/gtag";
import Tour360 from "@/components/landing/Tour360";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WhatsappLogo } from "@phosphor-icons/react";

const SEGMENTS = [
  "Loja Âncora / Satélite",
  "Estande em Contêiner",
  "Escritório Pronto",
  "Sala Comercial (VM Corporate)",
  "Gastronomia / Alimentação",
  "Serviços",
  "Outro",
];

const empty = { name: "", email: "", whatsapp: "", segment: "", message: "" };

export default function LeasingForm() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [form, setForm] = useState(empty);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.whatsapp || !form.segment) {
      toast.error("Preencha nome, e-mail, WhatsApp e segmento.");
      return;
    }
    const text =
      `Olá! Gostaria de mais informações sobre locação no VM Piabetá.\n` +
      `*Nome:* ${form.name}\n` +
      `*E-mail:* ${form.email}\n` +
      `*WhatsApp:* ${form.whatsapp}\n` +
      `*Segmento:* ${form.segment}\n` +
      `*Mensagem:* ${form.message || "-"}`;
    trackLeaseFormSubmit();
    openWhatsApp(text);
    toast.success("Abrindo o WhatsApp para falar com nossa equipe...");
  };

  const field = "w-full bg-transparent border-b border-vm-border focus:border-vm-gold outline-none py-3 text-vm-fg placeholder:text-vm-mutedFg/60 transition-colors";

  return (
    <section id="locacao" ref={ref} className="relative bg-vm-surface py-24 md:py-32" data-testid="leasing-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 lg:gap-20">
        <div>
          <motion.p initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ duration: 0.6 }} className="text-xs tracking-[0.4em] uppercase text-vm-gold mb-6">
            Locação · Seja um Lojista
          </motion.p>
          <motion.h2 initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }} className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="leasing-title">
            Faça Parte do <span className="gold-text italic">VM Piabetá</span>
          </motion.h2>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.15 }} className="mt-6 text-lg text-vm-mutedFg max-w-md leading-relaxed">
            Traga o seu negócio para o street mall de maior fluxo da região. Lojas, contêineres e salas comerciais disponíveis. Envie sua proposta e receba uma condição sob medida.
          </motion.p>
          <div className="mt-10 space-y-4">
            {["35 lojas âncoras e satélites", "30 estandes em contêineres", "Salas comerciais VM Corporate"].map((t) => (
              <div key={t} className="flex items-center gap-3 text-vm-mutedFg">
                <span className="h-1.5 w-1.5 rounded-full bg-vm-gold" />
                <span className="text-sm">{t}</span>
              </div>
            ))}
          </div>

          <div className="mt-10">
            <span className="text-xs tracking-[0.25em] uppercase text-vm-mutedFg block mb-3">Conheça por dentro — Tour 360°</span>
            <Tour360 compact />
          </div>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="space-y-7"
          data-testid="leasing-form"
        >
          <div>
            <label className="text-xs tracking-widest uppercase text-vm-mutedFg">Nome</label>
            <input className={field} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Seu nome completo" data-testid="lead-name" />
          </div>
          <div className="grid sm:grid-cols-2 gap-7">
            <div>
              <label className="text-xs tracking-widest uppercase text-vm-mutedFg">E-mail</label>
              <input type="email" className={field} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="voce@email.com" data-testid="lead-email" />
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-vm-mutedFg">WhatsApp</label>
              <input className={field} value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="(21) 90000-0000" data-testid="lead-whatsapp" />
            </div>
          </div>
          <div>
            <label className="text-xs tracking-widest uppercase text-vm-mutedFg block mb-2">Segmento de Interesse</label>
            <Select value={form.segment} onValueChange={(v) => set("segment", v)}>
              <SelectTrigger className="bg-transparent border-0 border-b border-vm-border rounded-none px-0 focus:ring-0 focus:border-vm-gold text-vm-fg h-auto py-3" data-testid="lead-segment">
                <SelectValue placeholder="Selecione um segmento" />
              </SelectTrigger>
              <SelectContent className="bg-vm-muted border-vm-border text-vm-fg">
                {SEGMENTS.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`segment-${s}`}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs tracking-widest uppercase text-vm-mutedFg">Mensagem</label>
            <textarea rows={3} className={`${field} resize-none`} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Conte-nos sobre o seu negócio" data-testid="lead-message" />
          </div>
          <button
            type="submit"
            className="btn-sweep relative z-0 w-full inline-flex items-center justify-center gap-2 bg-vm-gold text-vm-bg hover:text-vm-bg font-semibold px-7 py-4 rounded-full"
            data-testid="lead-submit"
          >
            Fale Conosco
            <WhatsappLogo size={20} weight="fill" />
          </button>
        </motion.form>
      </div>
    </section>
  );
}
